import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import type { PostFeedTypeFeature } from '@/features/feature-management/types/postTypeFeature';
import { ChannelSelection } from '@/features/post-editor/components/ChannelSelection';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import type { PostCreationChannelOption } from '@/features/post-editor/types/postCreationOptions';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { getAllowedMarketPostTypes } from '@/features/post-editor/utils/postCreationOptions';
import { resetChannelDependentFields } from '@/features/post-editor/utils/postFormReset';
import { useGetDorfHelferTypeName } from '@/features/posts/hooks/useGetDorfHelferTypeName';
import { DorfHelferTypeEnum } from '@/features/posts/types/dorfHelferType';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { SegmentedButtons } from '@/shared/components/segmented-button';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    marketTypeContainer: {
      paddingTop: 20,
      paddingHorizontal: 16,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    marketTypeSegmentedButton: {
      minWidth: '60%',
      maxWidth: '80%',
      alignSelf: 'center'
    }
  });

type Props = {
  groupId?: string;
  /** Locks the channel selection (edit mode). */
  readonly?: boolean;
  /** Whether the current sender is an organization. */
  isOrganization?: boolean;
  senderChannels?: PostCreationChannelOption[];
  senderChannelOptions?: PostFeedTypeFeature[];
};

/**
 * Channel selection plus the MARKET (offer/seeking) and SPECIAL_POST
 * (offer/request) sub-type toggles. All bound to the shared form context.
 */
export const PostChannelSection: FC<Props> = ({
  groupId,
  readonly,
  isOrganization = false,
  senderChannels,
  senderChannelOptions
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const form = useFormContext<PostFormValues>();
  const { control } = form;

  const channelField = useController({ control, name: 'channelType' });
  const marketField = useController({ control, name: 'marketType' });
  const dorfHelferField = useController({ control, name: 'dorfHelferType' });

  const channelType = channelField.field.value;

  const offerFeature = useGetPostTypeFeature(ClientPostTypeEnum.Offer);
  const seekingFeature = useGetPostTypeFeature(ClientPostTypeEnum.Seeking);
  const dorfHelferOfferName = useGetDorfHelferTypeName(
    DorfHelferTypeEnum.OFFER
  );
  const dorfHelferRequestName = useGetDorfHelferTypeName(
    DorfHelferTypeEnum.REQUEST
  );
  const allowedMarketTypes =
    readonly && !senderChannels
      ? [ClientPostTypeEnum.Offer, ClientPostTypeEnum.Seeking]
      : getAllowedMarketPostTypes(senderChannels ?? []);

  const handleChannelSelect = (channel: PostFeedType) => {
    // Switching the channel resets all channel-dependent context fields (date,
    // location, category, official flag, sub-types) to their defaults, so no
    // stale value from the previous channel is submitted. Authored content
    // (text, poll, media) is kept. The channel is set first so the reset
    // (built on form.getValues()) keeps it.
    channelField.field.onChange(channel);
    resetChannelDependentFields(form);
    if (channel === PostFeedType.MARKET && allowedMarketTypes.length === 1) {
      marketField.field.onChange(allowedMarketTypes[0]);
    }
  };

  const segmentedTheme = {
    colors: {
      secondaryContainer: theme.colors.tertiaryContainer,
      onSecondaryContainer: theme.colors.primary
    }
  };

  return (
    <>
      <ChannelSelection
        channelType={channelType}
        groupId={groupId}
        readonly={readonly}
        isOrganization={isOrganization}
        senderChannelOptions={senderChannelOptions}
        onChannelTypeSelect={handleChannelSelect}
      />

      {channelType === PostFeedType.MARKET && (
        <View style={styles.marketTypeContainer}>
          <SegmentedButtons<ClientPostTypeEnum>
            style={styles.marketTypeSegmentedButton}
            value={marketField.field.value}
            theme={segmentedTheme}
            onValueChange={marketField.field.onChange}
            buttons={allowedMarketTypes.map((type) => ({
              value: type,
              label:
                type === ClientPostTypeEnum.Offer
                  ? offerFeature.channelName
                  : seekingFeature.channelName
            }))}
          />
        </View>
      )}

      {channelType === PostFeedType.SPECIAL_POST && (
        <View style={styles.marketTypeContainer}>
          <SegmentedButtons<DorfHelferTypeEnum>
            style={styles.marketTypeSegmentedButton}
            value={dorfHelferField.field.value}
            theme={segmentedTheme}
            onValueChange={dorfHelferField.field.onChange}
            buttons={[
              { value: DorfHelferTypeEnum.OFFER, label: dorfHelferOfferName },
              {
                value: DorfHelferTypeEnum.REQUEST,
                label: dorfHelferRequestName
              }
            ]}
          />
        </View>
      )}
    </>
  );
};
